// Shared session ("SSO") across the Vacway platforms.
//
// Every platform client writes the session token it receives at login into a
// cookie scoped to .vacway.es, so a sister platform can adopt the session
// without a new login. The cookie token is NEVER used directly as the local
// session: the `type` claim is platform-specific (each platform overwrites it
// from its own DB at login), so boot exchanges it at POST login/token for a
// token minted by THIS platform's API — which also re-checks that the account
// exists here and is not blocked. Platforms the account lacks simply refuse
// the exchange and the app falls through to the login screen.
//
// Forced logouts (the 401 handler in middleware/api.js) deliberately do NOT
// clear the cookie; only an explicit logout does. A live session on a sister
// platform re-seeds the cookie on its next boot, so "logout everywhere" is
// best-effort by design.
import jwtDecode from 'jwt-decode'

import { API_ROOT } from '../config'

const COOKIE_NAME = 'vacway_sso'
const COOKIE_DOMAIN = '.vacway.es'
const COOKIE_MAX_AGE = 60 * 24 * 60 * 60 // seconds; matches the 60-day token exp

// sessionStorage markers driving the login screen's "request product info"
// button (set when this platform rejects the shared session for lack of an
// account here; the sent flag survives reloads within the tab).
const DENIED_KEY = 'vacway_sso_denied'
const INFO_SENT_KEY = 'vacway_sso_info_sent'

// The cookie only makes sense (and can only be set) on *.vacway.es; on any
// other host (localhost dev) every helper is a no-op.
function onVacwayDomain() {
  const host = window.location.hostname
  return host === 'vacway.es' || host.endsWith('.vacway.es')
}

export function writeSsoCookie(token) {
  if (!onVacwayDomain()) return
  document.cookie = COOKIE_NAME + '=' + encodeURIComponent(token) +
    '; Domain=' + COOKIE_DOMAIN + '; Path=/; Max-Age=' + COOKIE_MAX_AGE +
    '; Secure; SameSite=Lax'
}

export function clearSsoCookie() {
  if (!onVacwayDomain()) return
  document.cookie = COOKIE_NAME + '=; Domain=' + COOKIE_DOMAIN +
    '; Path=/; Max-Age=0; Secure; SameSite=Lax'
}

export function readSsoCookie() {
  if (!onVacwayDomain()) return null
  const entry = document.cookie.split('; ').find(c => c.indexOf(COOKIE_NAME + '=') === 0)
  return entry ? decodeURIComponent(entry.slice(COOKIE_NAME.length + 1)) : null
}

// Store a freshly issued session token: localStorage for this app plus the
// shared cookie for the sister platforms.
export function storeSessionToken(token) {
  localStorage.setItem('token', token)
  writeSsoCookie(token)
}

function tokenId(token) {
  try {
    return String(jwtDecode(token).id)
  } catch (e) {
    return null
  }
}

function isExpired(token) {
  try {
    const exp = jwtDecode(token).exp
    return !exp || exp * 1000 <= Date.now()
  } catch (e) {
    return true
  }
}

// Boot-time adoption. Resolves once localStorage holds whatever session this
// platform should hydrate from; only hits the network when the shared cookie
// carries a session this app does not already have.
export function adoptSsoSession() {
  if (!onVacwayDomain()) return Promise.resolve()

  // A previous visit's "no access here" marker is re-evaluated on every boot.
  try { sessionStorage.removeItem(DENIED_KEY) } catch (e) {}

  const local = localStorage.getItem('token')
  const localValid = local !== null && !isExpired(local)
  const cookie = readSsoCookie()

  if (!cookie || isExpired(cookie)) {
    // Keep the shared cookie alive from this platform's live session (some
    // browsers cap script-written cookies well below 60 days).
    if (localValid) writeSsoCookie(local)
    else if (cookie) clearSsoCookie()
    return Promise.resolve()
  }

  // Same identity already signed in here: keep this platform's own token
  // (its `type` claim is the locally correct one).
  if (localValid && tokenId(local) === tokenId(cookie)) {
    return Promise.resolve()
  }

  return fetch(API_ROOT + 'login/token', {
    method: 'POST',
    headers: { 'Content-type': 'application/json' },
    body: JSON.stringify({ token: cookie })
  })
    .then(response => response.json().then(json => {
      if (response.ok && json.token) {
        localStorage.setItem('token', json.token)
        writeSsoCookie(cookie) // rolling expiry for the shared session
      } else if (response.status === 401 || response.status === 403 || response.status === 422) {
        // This platform definitively rejected the shared session (no account
        // here, blocked, or the identity changed elsewhere): don't keep a
        // stale local session under another identity. The cookie stays — it
        // may still be good for the sister platforms.
        localStorage.removeItem('token')
        if (json && json.code === 'NO_PLATFORM_ACCOUNT') {
          // Signed-in Vacway user without this service: let the login screen
          // offer the "request product info" button.
          try {
            const info = jwtDecode(cookie)
            sessionStorage.setItem(DENIED_KEY, JSON.stringify({ email: info.email || '', fullname: info.fullname || '' }))
          } catch (e) {}
        }
      }
    }))
    .catch(() => {}) // network trouble: boot with whatever local state exists
}

// ---- "Request product info" support (login screen) ----

export function getSsoDeniedInfo() {
  try {
    const raw = sessionStorage.getItem(DENIED_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

export function wasInfoRequested() {
  try {
    return sessionStorage.getItem(INFO_SENT_KEY) === '1'
  } catch (e) {
    return false
  }
}

// Emails the sales team that this user wants the product. Authorized by the
// still-valid session token in the shared cookie (the platform that rejected
// the session left the cookie untouched).
export function requestPlatformInfo() {
  const cookie = readSsoCookie()
  if (!cookie) return Promise.reject(new Error('No shared session'))
  return fetch(API_ROOT + 'login/request-info', {
    method: 'POST',
    headers: { 'Content-type': 'application/json' },
    body: JSON.stringify({ token: cookie })
  }).then(response => {
    if (!response.ok) { throw new Error('Request failed') }
    try { sessionStorage.setItem(INFO_SENT_KEY, '1') } catch (e) {}
    return true
  })
}
